import { UIView, UIViewController } from "uikit";

export interface StepViewProtocol
{
    stepView: StepView;
}

/**
 * A step ("pill") of the booking form. Steps are instantiated only when their container exists in
 * the venue layout, numbered in DOM order, and communicate exclusively through the cart
 * (DLDB.shared.cart()), so a venue can reorder or hide them by editing its layout HTML.
 */
export interface BookingStep extends StepViewProtocol
{
    view: UIView;
    check ( ) : boolean;
    clear ( ) : void;
    // Whether the guest already made this step's selection. Unlike check(), it never touches the
    // error labels: it only drives which step pill is highlighted.
    hasValue ( ) : boolean;
}

export function isBookingStep ( vc: any ) : vc is BookingStep & UIViewController {
    return vc != null && typeof vc.check === "function" && typeof vc.clear === "function";
}

/**
 * The numbered steps of a page: the child view controllers that conform to BookingStep, in the
 * order their containers appear in the layout (DOM order). A controller with a StepView in its
 * layout but no conformance (a summary, like the product review) renders no badge.
 */
export function bookingStepsOf ( children: any[] ) : BookingStep[] {
    const steps = children.filter( vc => isBookingStep( vc ) ) as unknown as BookingStep[];

    const position = ( step: BookingStep ) => step.view?.layer?.contents as HTMLElement;
    steps.sort( ( a, b ) => {
        const la = position( a ), lb = position( b );
        if ( la == null || lb == null ) return 0;
        if ( la === lb ) return 0;
        return ( la.compareDocumentPosition( lb ) & Node.DOCUMENT_POSITION_FOLLOWING ) ? -1 : 1;
    } );

    return steps;
}

export class StepView extends UIView
{
    set selected ( value: boolean ) {
        if ( value == true ) {
            this.layer.addStyle( "selected" );
        }
        else {
            this.layer.removeStyle( "selected" );
        }
    }

    // Step number shown inside the bubble; assigned from the step order of the layout
    set number ( value: number ) {
        this.layer.contents.innerHTML = value.toString();
    }
}
